"use client";

import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const ELEMENTS = [
    { key: "image", icon: "gallery-bold-duotone", labelKey: "Rn_composer_el_image" },
    { key: "title", icon: "text-square-bold-duotone", labelKey: "Rn_composer_el_title" },
    { key: "list", icon: "list-check-bold", labelKey: "Rn_composer_el_list" },
    { key: "button", icon: "widget-bold", labelKey: "Rn_composer_el_button" },
];
const ORDER = ["image", "title", "list", "button"];

export default function RnComposer() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [on, setOn] = useState({
        image: true,
        title: true,
        list: false,
        button: false,
    });

    function toggle(key) {
        setOn((s) => ({ ...s, [key]: !s[key] }));
    }

    const anyOn = ORDER.some((k) => on[k]);

    return (
        <section id="rn-composer" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Rn_composer_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Rn_composer_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-red-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="widget-bold"
                            className="w-3.5 h-3.5 text-red-300"
                        />
                        Screen.js
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Rn_composer_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8 grid lg:grid-cols-2 gap-8 items-center">
                    {/* Contrôles */}
                    <div className="min-w-0 order-2 lg:order-1">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-red-300/90 mb-3">
                            {t("Rn_composer_hint")}
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                            {ELEMENTS.map((el) => {
                                const active = on[el.key];
                                return (
                                    <button
                                        key={el.key}
                                        type="button"
                                        onClick={() => toggle(el.key)}
                                        className={`flex items-center gap-2.5 rounded-xl px-3.5 py-3 ring-1 transition active:scale-95 ${
                                            active
                                                ? "bg-red-500/15 ring-red-400/50"
                                                : "bg-white/[0.04] ring-white/10 hover:bg-white/[0.07]"
                                        }`}
                                    >
                                        <span
                                            className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${
                                                active
                                                    ? "bg-red-500/20 text-red-300"
                                                    : "bg-white/5 text-gray-400"
                                            }`}
                                        >
                                            <SolarIcon
                                                name={el.icon}
                                                className="w-5 h-5"
                                            />
                                        </span>
                                        <span
                                            className={`flex-1 text-left text-[13px] font-semibold ${active ? "text-white" : "text-gray-400"}`}
                                        >
                                            {t(el.labelKey)}
                                        </span>
                                        <SolarIcon
                                            name={
                                                active
                                                    ? "check-circle-bold"
                                                    : "add-circle-bold"
                                            }
                                            className={`w-5 h-5 shrink-0 ${active ? "text-red-400" : "text-gray-500"}`}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Téléphone */}
                    <div className="min-w-0 order-1 lg:order-2 flex justify-center">
                        <div className="w-[238px] rounded-[2.3rem] bg-slate-950 ring-1 ring-white/15 shadow-2xl shadow-black/50 p-2.5">
                            <div className="relative rounded-[1.8rem] bg-white overflow-hidden">
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-950 rounded-b-2xl z-10" />
                                <div className="flex items-center justify-between px-5 pt-2 pb-1 text-[10px] font-semibold text-slate-700">
                                    <span>9:41</span>
                                    <span className="w-3.5 h-2 rounded-[2px] border border-slate-400" />
                                </div>

                                <div className="px-4 pb-5 pt-3 min-h-[330px]">
                                    {!anyOn && (
                                        <div className="h-[300px] flex flex-col items-center justify-center text-center gap-2 text-slate-300">
                                            <SolarIcon
                                                name="widget-2-bold"
                                                className="w-8 h-8"
                                            />
                                            <span className="text-[12px] text-slate-400 px-6">
                                                {t("Rn_composer_empty")}
                                            </span>
                                        </div>
                                    )}
                                    <motion.div layout className="flex flex-col gap-3">
                                        <AnimatePresence initial={false}>
                                            {ORDER.filter((k) => on[k]).map(
                                                (k) => (
                                                    <motion.div
                                                        key={k}
                                                        layout
                                                        initial={
                                                            reduce
                                                                ? false
                                                                : {
                                                                      opacity: 0,
                                                                      scale: 0.9,
                                                                  }
                                                        }
                                                        animate={{
                                                            opacity: 1,
                                                            scale: 1,
                                                        }}
                                                        exit={{
                                                            opacity: 0,
                                                            scale: 0.9,
                                                        }}
                                                        transition={{
                                                            duration: reduce
                                                                ? 0
                                                                : 0.25,
                                                        }}
                                                    >
                                                        {k === "image" && (
                                                            <div className="h-24 rounded-xl bg-gradient-to-br from-red-400 to-orange-300 flex items-center justify-center">
                                                                <SolarIcon
                                                                    name="gallery-bold-duotone"
                                                                    className="w-8 h-8 text-white/80"
                                                                />
                                                            </div>
                                                        )}
                                                        {k === "title" && (
                                                            <div>
                                                                <div className="text-[16px] font-bold text-slate-900">
                                                                    {t(
                                                                        "Rn_composer_phone_title",
                                                                    )}
                                                                </div>
                                                                <div className="mt-1.5 h-2 rounded-full bg-slate-200 w-3/4" />
                                                            </div>
                                                        )}
                                                        {k === "list" && (
                                                            <div className="flex flex-col gap-2">
                                                                {[0, 1, 2].map(
                                                                    (r) => (
                                                                        <div
                                                                            key={
                                                                                r
                                                                            }
                                                                            className="flex items-center gap-2.5"
                                                                        >
                                                                            <span className="w-8 h-8 rounded-lg bg-slate-100 shrink-0" />
                                                                            <div className="flex-1 space-y-1.5">
                                                                                <div className="h-2 rounded-full bg-slate-200 w-2/3" />
                                                                                <div className="h-2 rounded-full bg-slate-100 w-1/2" />
                                                                            </div>
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        )}
                                                        {k === "button" && (
                                                            <div className="rounded-xl bg-red-500 text-white text-center py-2.5 text-[13px] font-semibold">
                                                                {t(
                                                                    "Rn_composer_phone_button",
                                                                )}
                                                            </div>
                                                        )}
                                                    </motion.div>
                                                ),
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Rn_composer_caption")}
            </p>
        </section>
    );
}
